// WebSocket protocol (spec §6.3). JSON messages with a `type` field.
// decodeClientMessage() is the server's first line of defence: it checks
// shape only; op contents are validated against the room's state.
import type { BoardObject, Op, ServerOp } from './types.ts';
import { ITEM_ID } from './cards.ts';

export type Cursor = [number, number] | null;

export interface PresenceState {
  cursor?: Cursor;           // world coords; null = pointer left the board
  selection?: string[];
}

export interface Peer extends PresenceState {
  userId: string;
  name: string;
  color: string;
}

// 'unpublished': a public link stopped working; 'busy': too many public viewers for live updates (spec §17.2).
export type KickReason = 'removed' | 'access-changed' | 'board-deleted' | 'signed-out' | 'unpublished' | 'busy';

export type ClientMessage =
  | { type: 'join'; boardId: string; lastSeq?: number }
  | { type: 'ops'; ops: { clientOpId: string; op: Op }[] }
  | { type: 'presence'; cursor?: Cursor; selection?: string[] }
  | { type: 'ping' };

export type ServerMessage =
  | { type: 'init'; snapshot: BoardObject[]; ops: ServerOp[]; seq: number; members: Peer[]; you: Peer & { canEdit: boolean } }
  | { type: 'resume'; ops: ServerOp[]; seq: number; members: Peer[] }
  | { type: 'ops'; ops: ServerOp[] }
  | { type: 'reject'; clientOpIds: string[]; reason: string }
  | ({ type: 'presence' } & Peer)
  | { type: 'peer-left'; userId: string }
  | { type: 'kicked'; reason: KickReason }
  | { type: 'pong' };

export const MAX_OPS_PER_MESSAGE = 1000;
export const MAX_SELECTION = 1000;

const ID = /^[A-Za-z0-9_-]{1,64}$/;
type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => typeof v === 'object' && v !== null && !Array.isArray(v);
const isFiniteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function isOp(v: unknown): v is Op {
  if (!isRec(v)) return false;
  switch (v.t) {
    case 'create': return isRec(v.obj);
    case 'update': return typeof v.id === 'string' && ID.test(v.id) && isRec(v.props);
    case 'delete': return typeof v.id === 'string' && ID.test(v.id);
    case 'item': return typeof v.id === 'string' && ID.test(v.id) && typeof v.itemId === 'string' && ITEM_ID.test(v.itemId) &&
      (v.props === null || isRec(v.props));
    default: return false;
  }
}

/** Parses and shape-checks a client message; null if malformed. */
export function decodeClientMessage(raw: string): ClientMessage | null {
  let m: unknown;
  try {
    m = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRec(m)) return null;
  switch (m.type) {
    case 'join':
      if (typeof m.boardId !== 'string' || !ID.test(m.boardId)) return null;
      if (m.lastSeq !== undefined && !(Number.isInteger(m.lastSeq) && (m.lastSeq as number) >= 0)) return null;
      return { type: 'join', boardId: m.boardId, lastSeq: m.lastSeq as number | undefined };
    case 'ops': {
      if (!Array.isArray(m.ops) || m.ops.length === 0 || m.ops.length > MAX_OPS_PER_MESSAGE) return null;
      for (const e of m.ops) {
        if (!isRec(e) || typeof e.clientOpId !== 'string' || !ID.test(e.clientOpId) || !isOp(e.op)) return null;
      }
      return { type: 'ops', ops: m.ops as { clientOpId: string; op: Op }[] };
    }
    case 'presence': {
      const out: ClientMessage = { type: 'presence' };
      if (m.cursor !== undefined) {
        if (m.cursor !== null && !(Array.isArray(m.cursor) && m.cursor.length === 2 && m.cursor.every(isFiniteNum))) return null;
        out.cursor = m.cursor as Cursor;
      }
      if (m.selection !== undefined) {
        if (!Array.isArray(m.selection) || m.selection.length > MAX_SELECTION ||
          !m.selection.every((s) => typeof s === 'string' && ID.test(s))) return null;
        out.selection = m.selection as string[];
      }
      return out;
    }
    case 'ping':
      return { type: 'ping' };
    default:
      return null;
  }
}

/** Stable per-user colour for cursors and avatars. */
const PEER_COLORS = ['#E5484D', '#F76B15', '#D6A100', '#30A46C', '#12A594', '#0090FF', '#3E63DD', '#8E4EC6', '#D6409F', '#6E56CF'];
export function peerColor(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) | 0;
  return PEER_COLORS[Math.abs(h) % PEER_COLORS.length]!;
}
